import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, UserPlus, Clock, Check, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { Avatar } from "./Avatar";
import { RequestButtons } from "./FriendRequests";
import { useFriendActions } from "./useFriendActions";

const Action = ({ p, onView }) => {
  const { add, cancel } = useFriendActions();
  const base = "flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors";
  if (p.relation === "friend")
    return <button onClick={() => onView(p.user_id)} className={`${base} border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10`} data-testid={`person-view-${p.user_id}`}><Check size={12} /> Friends · view</button>;
  if (p.relation === "sent")
    return <button onClick={() => cancel(p)} className={`${base} border border-white/15 text-slate-400 hover:text-red-300`} data-testid={`person-cancel-${p.user_id}`}><Clock size={12} /> Requested · cancel</button>;
  if (p.relation === "received") return <RequestButtons p={p} small />;
  return <button onClick={() => add(p)} className={`${base} bg-sky-400 text-[#06121c] hover:bg-sky-300`} data-testid={`person-add-${p.user_id}`}><UserPlus size={12} /> Add friend</button>;
};

const VisibilityToggle = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const on = data?.discoverable ?? true;
  const toggle = async (v) => {
    qc.setQueryData(["settings"], (s) => ({ ...s, discoverable: v }));
    await api.saveSettings({ discoverable: v });
  };
  return (
    <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer" data-testid="discoverable-toggle-label">
      <Switch checked={on} onCheckedChange={toggle} data-testid="discoverable-toggle" />
      {on ? "Friends can find me by email or short name" : <span className="flex items-center gap-1"><EyeOff size={12} /> Hidden from search</span>}
    </label>
  );
};

export const PeopleFinder = ({ onView }) => {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 400);
    return () => clearTimeout(t);
  }, [q]);
  const { data: people = [], isFetching } = useQuery({ queryKey: ["people", debounced], queryFn: () => api.people(debounced), enabled: !!debounced.trim() });

  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0D131F] p-4 sm:p-5" data-testid="people-finder">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="font-display font-semibold text-lg">Add a friend</p>
        <VisibilityToggle />
      </div>
      <div className="relative mt-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Their email or short name, e.g. vikingur" className="pl-9" data-testid="people-search-input" />
      </div>
      <ul className="mt-3 max-h-[300px] overflow-y-auto divide-y divide-white/[0.05]" data-testid="people-list">
        {(debounced.trim() ? people : []).map((p) => (
          <li key={p.user_id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`person-${p.user_id}`}>
            <span className="flex items-center gap-2.5 text-sm min-w-0"><Avatar f={p} size="w-8 h-8" /> <span className="truncate">{p.name}</span></span>
            <Action p={p} onView={onView} />
          </li>
        ))}
        {!people.length && !isFetching && <li className="py-6 text-center text-sm text-slate-500" data-testid="people-empty">{debounced.trim() ? "No one with that email or short name" : "Type the exact email or short name of someone you know"}</li>}
      </ul>
    </div>
  );
};
